import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { useCustomer } from 'hooks/Customers/useCustomer';
import { CustomerDepartmentsStructureProps, PlannerSlot } from '../types';
import { useParams } from 'react-router-dom';
import Spinner from 'components/Spinner';
import { Space, Table, Modal } from 'antd';
import { useIntl } from 'react-intl';
import useQueryApiClient from 'utils/useApiClient';
import { Button } from 'ui';
import { RequestSelect } from 'components/Form';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const mapStateToProps = (state: any) => ({ session: state.session });

const CustomerOrganizationalStructure: React.FC<{ session: any }> = ({ session }) => {
    const { getDepartments, saveResourcePlanner, loading } = useCustomer();
    const [structure, setStructure] = useState<CustomerDepartmentsStructureProps[]>([]);
    const [maxPlannerSlots, setMaxPlannerSlots] = useState<number>(8);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [selectedSlots, setSelectedSlots] = useState<(number | null)[]>([]);
    const [visibleSlotCount, setVisibleSlotCount] = useState<number>(1);

    const canEdit = session?.special_rights?.includes('edit_resource_planners')
        || session?.active_user_type === 'SUPER_ADMIN';

    const params = useParams();
    const customerId = params.id;
    const intl = useIntl();
    const { confirm } = Modal;

    useEffect(() => {
        initialLoad();
    }, []);

    const initialLoad = () => {
        if (customerId) {
            loadDepartments(customerId);
        }
    };

    const loadDepartments = async (id: string) => {
        const result = await getDepartments(id);
        setStructure(result.data);
        setMaxPlannerSlots(result.maxPlannerSlots);
    };

    const startEditing = useCallback((record: any) => {
        const planners: PlannerSlot[] = record.planners || [];
        const slots: (number | null)[] = planners.map((p: PlannerSlot) => p.type_id);

        setSelectedSlots(slots);
        setEditingRowId(record.id);
        setVisibleSlotCount(Math.min(Math.max(planners.length + 1, 1), maxPlannerSlots));
    }, [maxPlannerSlots]);

    const handleSlotChange = useCallback((slotIndex: number, value: number | undefined) => {
        setSelectedSlots((prev) => {
            const next = [...prev];
            next[slotIndex] = value ?? null;
            return next;
        });
    }, []);

    const addSlot = useCallback(() => {
        setVisibleSlotCount((prev) => Math.min(prev + 1, maxPlannerSlots));
    }, [maxPlannerSlots]);

    const handleSave = async (record: any) => {
        if (!customerId) return;

        const result = await saveResourcePlanner(customerId, {
            id: record.id,
            user_type_ids: selectedSlots.slice(0, visibleSlotCount),
        });

        if (result) {
            setStructure(result.data);
            setMaxPlannerSlots(result.maxPlannerSlots);
            setEditingRowId(null);
        }
    };

    const handleDeleteAll = (record: any) => {
        confirm({
            title: intl.formatMessage({ id: 'general.attention' }),
            content: intl.formatMessage({ id: 'confirmation.are_you_sure' }),
            icon: <ExclamationCircleOutlined />,
            okText: intl.formatMessage({ id: 'general.yes' }),
            cancelText: intl.formatMessage({ id: 'general.no' }),
            onOk: async () => {
                if (!customerId) return;

                const result = await saveResourcePlanner(customerId, {
                    id: record.id,
                    user_type_ids: [],
                });

                if (result) {
                    setStructure(result.data);
                    setMaxPlannerSlots(result.maxPlannerSlots);
                }

                setSelectedSlots([]);
                setVisibleSlotCount(1);
                setEditingRowId(null);
            },
        });
    };

    const renderPlannerView = (record: any) => {
        const planners: PlannerSlot[] = record.planners || [];
        const assigned = planners.filter((p: PlannerSlot) => p.user_type_id);

        return (
            <div className={'flex justify-between align-center'}>
                <div>
                    {assigned.map((p: PlannerSlot) => (
                        <div key={p.id} className={'structure-tag'} style={{ marginBottom: 2 }}>
                            {p.fullName || p.email || `ID: ${p.user_id}`}
                        </div>
                    ))}
                </div>
                {canEdit && (
                    <Button
                        type={assigned.length > 0 ? 'link' : 'dashed'}
                        onClick={() => startEditing(record)}
                        message={assigned.length > 0 ? 'general.edit' : 'general.add'}
                    />
                )}
            </div>
        );
    };

    const renderPlannerEdit = (record: any) => {
        const planners: PlannerSlot[] = record.planners || [];

        return (
            <div>
                {Array.from({ length: visibleSlotCount }).map((_, index) => {
                    const existingPlanner = planners[index];

                    return (
                        <div key={index} style={{ marginBottom: 8 }}>
                            <RequestSelect
                                isForm={false}
                                defaultValue={
                                    existingPlanner?.type_id
                                        ? String(existingPlanner.type_id)
                                        : null
                                }
                                selectClassName="request-select"
                                allowClear
                                onChange={(typeId: number | undefined) =>
                                    handleSlotChange(index, typeId)
                                }
                                url={'/api/v1/edu/users/mass-search'}
                                initialValueData={
                                    existingPlanner?.user_id
                                        ? { [existingPlanner.type_id]: existingPlanner.email }
                                        : null
                                }
                                param={[
                                    `customerId=${customerId}&key=type_id&bothRoles=false&onlyCustomer=true&supervision=true`,
                                ]}
                                column
                                name={`planner_${index}`}
                                showSearch
                            />
                        </div>
                    );
                })}
                <Space size={8}>
                    {visibleSlotCount < maxPlannerSlots && (
                        <Button
                            type={'dashed'}
                            onClick={addSlot}
                            message='general.add_planner'
                        />
                    )}
                    <Button
                        type={'link'}
                        onClick={() => handleSave(record)}
                        message='general.save'
                    />
                    <Button
                        type={'link'}
                        onClick={() => setEditingRowId(null)}
                        message='general.cancel'
                    />
                    {planners.length > 0 && (
                        <Button
                            type={'link'}
                            danger
                            onClick={() => handleDeleteAll(record)}
                            message='general.delete_all'
                        />
                    )}
                </Space>
            </div>
        );
    };

    const columns = [
        {
            title: intl.formatMessage({ id: 'general.departments' }),
            dataIndex: 'title',
        },
        {
            title: intl.formatMessage({ id: 'general.organizational_unit' }),
            dataIndex: 'unit_title',
        },
        {
            title: intl.formatMessage({ id: 'general.usage' }),
            dataIndex: 'count',
        },
        {
            title: intl.formatMessage({ id: 'general.resource_planners' }),
            dataIndex: 'planners',
            width: '55%',
            render: (_text: string, record: any) => {
                if (editingRowId === record.id) {
                    return renderPlannerEdit(record);
                }
                return renderPlannerView(record);
            },
        },
        {
            key: 'actions',
            render: (_text: string, record: any) => {
                if (record.count === 0) {
                    return (
                        <Button
                            type={'link'}
                            onClick={() => {
                                warnDeletion(
                                    record?.department_id
                                        ? record.department_id
                                        : record.id,
                                    record?.department_id
                                        ? 'departments'
                                        : 'organizational-units'
                                );
                            }}
                            message='general.delete'
                        />
                    );
                }
            },
        },
    ];

    const warnDeletion = async (id: number, tab: string) => {
        confirm({
            title: intl.formatMessage({ id: 'general.attention' }),
            content: intl.formatMessage({ id: 'confirmation.are_you_sure' }),
            icon: <ExclamationCircleOutlined />,
            okText: intl.formatMessage({ id: 'general.yes' }),
            cancelText: intl.formatMessage({ id: 'general.no' }),

            onOk() {
                deleteStructure({}, { tab: tab, id: id });
            },
        });
    };

    const { appendData: deleteStructure } = useQueryApiClient({
        request: {
            url: `/api/v1/customers/${customerId}/structure/:tab/:id/delete`,
            method: 'DELETE',
        },
        onSuccess: (): void => {
            initialLoad();
        },
    });

    return (
        <Spinner spinning={loading}>
            <Table columns={columns} rowKey={'id'} pagination={false} dataSource={structure} />
        </Spinner>
    );
};

export default connect(mapStateToProps)(CustomerOrganizationalStructure);
